import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  useTheme,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../theme";
import { useNavigate, useParams } from "react-router-dom";
import PermissionGroupService from "../../services/tenantServices/PermissionGroupService";
import TenantLayout from "../../layouts/TenantLayout";
import SecurityIcon from "@mui/icons-material/Security";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { tenantPanelContentSx } from "./tenantPanelContentSx";

export default function ViewPermissionGroup() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();
  const permissionService = new PermissionGroupService();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGroupData();
  }, [id]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const data = await permissionService.getGroupById(id);
      setGroup(data);
    } catch (error) {
      console.error("Error fetching group data:", error);
      navigate("/tenant/permission-groups");
    } finally {
      setLoading(false);
    }
  };

  const getPermissions = () => {
    if (!group) return [];
    
    const permissionsList = [];
    Object.keys(group).forEach(key => {
      if (key.startsWith('can_')) {
        // Admin: sadece scoring_formulas_* alanlarını gizle (create ekranında yok)
        if (group.type === 'admin' && key.includes('scoring_formulas')) return;
        // Admin: own alanları yine gizle
        if (group.type === 'admin' && key.startsWith('can_own_')) return;
        
        const parts = key.replace('can_', '').split('_');
        const operation = parts.pop();
        const resource = parts.join(' ');
        
        permissionsList.push({
          key,
          resource: resource.charAt(0).toUpperCase() + resource.slice(1),
          operation: operation.charAt(0).toUpperCase() + operation.slice(1),
          enabled: group[key],
        });
      }
    });
    
    return permissionsList;
  };

  const groupPermissionsByResource = () => {
    const permissions = getPermissions();
    const grouped = {};
    
    permissions.forEach(perm => {
      if (!grouped[perm.resource]) {
        grouped[perm.resource] = [];
      }
      grouped[perm.resource].push(perm);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <TenantLayout contentSx={tenantPanelContentSx}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={50} sx={{ color: colors.blueAccent[500], mb: 2 }} />
            <Typography variant="h5" sx={{ color: "#64748b" }}>
              Loading group data...
            </Typography>
          </Box>
        </Box>
      </TenantLayout>
    );
  }

  if (!group) {
    return null;
  }

  const groupedPermissions = groupPermissionsByResource();
  const enabledCount = getPermissions().filter(p => p.enabled).length;
  const totalCount = getPermissions().length;

  return (
    <TenantLayout contentSx={tenantPanelContentSx}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/tenant/permission-groups")}
              sx={{
                backgroundColor: `${colors.grey[700]}40`,
                "&:hover": {
                  backgroundColor: `${colors.grey[600]}60`,
                },
              }}
            >
              <ArrowBackIcon sx={{ color: colors.grey[300] }} />
            </IconButton>
            
            <SecurityIcon sx={{ fontSize: 40, color: colors.blueAccent[500] }} />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, #1e293b 0%, ${colors.blueAccent[600]} 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {group.label}
                </Typography>
                {group.is_default && (
                  <Tooltip title="Default group - cannot be deleted">
                    <Chip
                      icon={<LockIcon sx={{ fontSize: 14 }} />}
                      label="Default"
                      size="small"
                      sx={{
                        backgroundColor: `${colors.blueAccent[800]}40`,
                        color: colors.blueAccent[300],
                        fontWeight: 600,
                        border: `1px solid ${colors.blueAccent[500]}40`,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                {group.description || "No description provided"}
              </Typography>
            </Box>
          </Box>

          {!group.is_default && (
            <Tooltip title="Edit Group">
              <IconButton
                onClick={() => navigate(`/tenant/permission-groups/edit/${group.id}`)}
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: `${colors.greenAccent[700]}40`,
                  border: `1px solid ${colors.greenAccent[500]}40`,
                  "&:hover": {
                    backgroundColor: `${colors.greenAccent[600]}60`,
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <EditIcon sx={{ fontSize: 22, color: colors.greenAccent[300] }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Group Info */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
                border: `1px solid ${colors.grey[800]}30`,
                borderRadius: "16px",
                height: "100%",
              }}
            >
              <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, mb: 3 }}>
                📋 Group Details
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Type */}
                <Box>
                  <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 1 }}>
                    Group Type
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: group.type === 'admin' 
                          ? `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`
                          : `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {group.type === 'admin' ? (
                        <AdminPanelSettingsIcon sx={{ color: "white", fontSize: 22 }} />
                      ) : (
                        <MedicalServicesIcon sx={{ color: "white", fontSize: 22 }} />
                      )}
                    </Box>
                    <Chip
                      label={group.type.toUpperCase()}
                      sx={{
                        backgroundColor: group.type === 'admin' ? colors.blueAccent[700] : colors.greenAccent[700],
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ backgroundColor: `${colors.grey[700]}40` }} />

                {/* Internal Name */}
                <Box>
                  <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                    Internal Name
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.grey[100], fontWeight: 600, fontFamily: "monospace" }}>
                    {group.name}
                  </Typography>
                </Box>

                <Divider sx={{ backgroundColor: `${colors.grey[700]}40` }} />

                {/* Permissions Count */}
                <Box>
                  <Typography variant="caption" sx={{ color: colors.grey[300], display: "block", mb: 0.5 }}>
                    Active Permissions
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h4" sx={{ color: colors.greenAccent[400], fontWeight: 700 }}>
                      {enabledCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                      / {totalCount} total
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Permissions Matrix */}
          <Grid item xs={12} md={8}>
            <Paper
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
                border: `1px solid ${colors.grey[800]}30`,
                borderRadius: "16px",
              }}
            >
              <Typography variant="h5" sx={{ color: colors.grey[100], fontWeight: 700, mb: 3 }}>
                🔐 Permissions Overview
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {Object.entries(groupedPermissions).map(([resource, permissions]) => (
                  <Card
                    key={resource}
                    sx={{
                      background: `${colors.primary[400]}`,
                      borderRadius: "12px",
                      border: `1px solid ${colors.grey[700]}40`,
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.grey[100],
                          fontWeight: 700,
                          mb: 1.5,
                          fontSize: "0.95rem",
                        }}
                      >
                        {resource}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {permissions.map(perm => (
                          <Chip
                            key={perm.key}
                            icon={
                              perm.enabled ? (
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <CancelIcon sx={{ fontSize: 16 }} />
                              )
                            }
                            label={perm.operation}
                            size="small"
                            sx={{
                              backgroundColor: perm.enabled 
                                ? `${colors.greenAccent[100]}`
                                : `${colors.grey[200]}`,
                              color: perm.enabled 
                                ? colors.greenAccent[700]
                                : "#64748b",
                              fontWeight: 600,
                              border: `1px solid ${perm.enabled 
                                ? colors.greenAccent[500] 
                                : colors.grey[400]}40`,
                              "& .MuiChip-icon": {
                                color: perm.enabled 
                                  ? colors.greenAccent[600]
                                  : "#94a3b8",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </TenantLayout>
  );
}




